import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { CUSTOMER_ROUTES_GROUP } from '@/router/customerRoutes'
import { SIGNUP_ROUTES_GROUP } from './signupRoutes'
import { OLD_ROUTES } from './oldRoutes'
import { AUTH_ROUTES } from './authRoutes'
import type { RouteRecordWithMeta } from './types'
import { validateRoutes } from './validation'
import { stripeCouponCapture } from './guards/stripeCouponCapture'
import { brandingCodeCapture } from './guards/brandingCodeCapture'
import { customTokenAuth } from './guards/customTokenAuth'
import { crossOriginIsolated } from './guards/crossOriginIsolated'
import { useAnalyticsStore } from '@/stores/analytics'

// Dynamic imports for all components
import { waitForStores } from './guards/waitForStores'
import { redirects } from './guards/redirects'
import { adminRoutes } from './adminRoutes'
import { waitForSignup } from './guards/waitForSignup'
import { handleChunkLoadError, isChunkLoadError } from '@/utils/versionCheck'

// Dynamic import for ReferralCodeView
const ReferralCodeView = () => import('@/views/ReferralCodeView.vue')
const PasswordRecoveryBridge = () => import('@/components/signup/PasswordRecoveryBridge.vue')

export const routes: RouteRecordRaw[] = [
  {
    path: '/',
    component: { template: '<div></div>' },
    meta: {},
  },
  // Referral code route - must be before other routes to avoid conflicts
  {
    path: '/r/:referralCode',
    name: 'referral-code',
    component: ReferralCodeView,
    meta: { requiresAuth: false, hideNavbar: true },
  },
  {
    path: '/password-recovery/:token',
    name: 'password-recovery',
    component: PasswordRecoveryBridge,
    meta: { requiresAuth: false, hideNavbar: true },
  },
  OLD_ROUTES,
  CUSTOMER_ROUTES_GROUP,
  SIGNUP_ROUTES_GROUP,
  AUTH_ROUTES,
  ...adminRoutes,
]

validateRoutes(routes as unknown as RouteRecordWithMeta[])

const CLINICAL_CONSULTATION_TEST_PATH_PREFIX = '/admin/clinical-consultation-test'

function isClinicalConsultationTestPath(path: string): boolean {
  return (
    path === CLINICAL_CONSULTATION_TEST_PATH_PREFIX ||
    path.startsWith(`${CLINICAL_CONSULTATION_TEST_PATH_PREFIX}/`)
  )
}

function shouldPreserveScrollOnNavigation(fromPath: string, toPath: string): boolean {
  return isClinicalConsultationTestPath(fromPath) && isClinicalConsultationTestPath(toPath)
}

const router = createRouter({
  history: createWebHistory(),
  routes: routes as RouteRecordRaw[],
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    if (shouldPreserveScrollOnNavigation(from.path, to.path)) {
      return false
    }
    return { left: 0, top: 0 }
  },
})

// Navigation guards - order matters!
// customTokenAuth must run before waitForStores to authenticate user first
router.beforeEach(crossOriginIsolated)
router.beforeEach(customTokenAuth)
router.beforeEach(waitForStores)
router.beforeEach(waitForSignup)
router.beforeEach(stripeCouponCapture)
router.beforeEach(brandingCodeCapture)
router.beforeEach(redirects)

router.onError((error) => {
  if (!isChunkLoadError(error)) return
  void handleChunkLoadError(error)
})

// Track page views after navigation completes
router.afterEach((to, from) => {
  const preserveScroll = shouldPreserveScrollOnNavigation(from.path, to.path)

  if (to.path !== from.path && typeof window !== 'undefined' && !preserveScroll) {
    window.requestAnimationFrame(() => {
      const contentContainer = document.querySelector<HTMLElement>('#app-container .content')
      if (contentContainer) {
        contentContainer.scrollTo({ top: 0, left: 0, behavior: 'auto' })
        return
      }
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
    })
  }

  // Firebase Analytics owns product page views. Do not also push to dataLayer;
  // use a GTM History Change trigger if marketing needs SPA routes.
  useAnalyticsStore().events.trackPageView({
    page_path: to.path,
    page_title: String(to.name || to.path),
    page_location: window.location.href,
    from_page: from.path || undefined,
  })
})

export default router
